import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react';
import { useGlobalContext } from 'GlobalContext';
import { useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { changeManyContractDueDate } from 'services/contract';
import { Contract } from 'shared/models/contract';
type DueDay = 5 | 10 | 15 | 20;

interface ChangeManyDueDateModalProps {
  isOpen: boolean;
  onClose(): void;
  contracts: Contract[];
}

const ChangeManyDueDateModal: React.FC<ChangeManyDueDateModalProps> = ({
  isOpen,
  onClose: onCloseFromProps,
  contracts,
}) => {
  const { confirm } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [dueDate, setDueDate] = useState<DueDay>(5);

  const activeContracts = contracts.filter(
    (c) => c.status === 1 && !c.deleted_at,
  );

  const inactiveCount = contracts.length - activeContracts.length;

  const onClose = () => {
    onCloseFromProps();
    setLoading(false);
    setDueDate(5);
  };

  const submit = () => {
    if (!dueDate || activeContracts.length === 0) {
      return toast.error('Nenhum contrato ativo encontrado para alteração.');
    }

    const contractIds = activeContracts.map((c) => c.id!);

    confirm(
      `Alterar vencimento de ${activeContracts.length} contrato(s) para dia ${dueDate}`,
      'Deseja continuar?',
      async () => {
        setLoading(true);
        try {
          await changeManyContractDueDate(contractIds, dueDate);
          toast.success(
            `Vencimento alterado para ${activeContracts.length} contrato(s)`,
          );
          onClose();
        } catch (e) {
          const axiosError = e as AxiosError;
          toast.error('Falha ao alterar data de vencimento dos contratos');
          console.error(
            'Falha ao alterar data de vencimento dos contratos',
            axiosError?.response?.data
              ? JSON.stringify(axiosError?.response?.data, null, 2)
              : JSON.stringify(axiosError?.response, null, 2),
          );
        } finally {
          setLoading(false);
        }
      },
    );
  };

  return (
    <Modal
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton isDisabled={loading} />
        <ModalHeader>Alterar Vencimento em Lote</ModalHeader>

        <ModalBody>
          <Card bg="blue.100" mb="1rem">
            <CardBody>
              <HStack gap="1rem">
                <Icon as={MdInfoOutline} color="blue.500" />
                <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                  Todos os contratos <strong>ativos</strong> terão o vencimento
                  alterado. <br />
                  Ao realizar a alteração do vencimento, a próxima cobrança de
                  cada contrato terá o valor reajustado.
                </Text>
              </HStack>
            </CardBody>
          </Card>

          {inactiveCount > 0 && (
            <Card bg="yellow.100" mb="1rem">
              <CardBody>
                <HStack gap="1rem">
                  <Icon as={MdInfoOutline} color="yellow.500" />
                  <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                    Contratos inativos ou cancelados não serão afetados por esta
                    alteração.
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          )}

          <Text fontSize="0.85rem" color="gray.500" mb="0.5rem">
            {activeContracts.length} contrato(s) serão alterados.
          </Text>

          <FormControl mt="0.5rem">
            <FormLabel fontSize="0.8rem" color="gray.500">
              Novo dia de vencimento
            </FormLabel>
            <Select
              id="select-many-due-date"
              value={String(dueDate)}
              onChange={(e) => setDueDate(Number(e.target.value) as DueDay)}
              isDisabled={loading}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
            </Select>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            onClick={submit}
            isDisabled={loading || activeContracts.length === 0}
            colorScheme="yooga"
            w="100%"
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeManyDueDateModal;
